import React, { useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { StarIcon } from '@heroicons/react/24/solid';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const SocialProofSection: React.FC = () => {
  const { translations } = useLanguage();
  const scrollRef = useRef<HTMLDivElement>(null);
  
  const reviews = translations.socialProof?.reviews || [];
  const title = translations.socialProof?.title || "Social Proof";

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth, scrollWidth } = scrollRef.current;
      
      if (direction === 'left') {
        if (scrollLeft <= 0) {
          // If at the start, scroll to the end
          scrollRef.current.scrollTo({ left: scrollWidth, behavior: 'smooth' });
        } else {
          scrollRef.current.scrollTo({ left: scrollLeft - clientWidth, behavior: 'smooth' });
        }
      } else {
        // Check if we are at the end (with a small buffer for subpixel issues)
        if (scrollLeft + clientWidth >= scrollWidth - 10) {
          // If at the end, scroll to the beginning
          scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          scrollRef.current.scrollTo({ left: scrollLeft + clientWidth, behavior: 'smooth' });
        }
      }
    }
  };

  if (reviews.length === 0) return null;

  return (
    <section className="py-24 bg-gradient-to-b from-white to-blue-50 overflow-hidden" id="social-proof">
      <div className="container-custom">
        <h2 className="text-navy text-center mb-16 text-4xl md:text-5xl font-title leading-tight">
          {title}
        </h2>
        
        <div className="relative max-w-6xl mx-auto">
          {/* Desktop Navigation Arrows */}
          <button 
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 z-10 p-4 bg-white shadow-xl rounded-full text-navy hover:text-mint transition-all duration-300 hidden xl:block border border-gray-100 group"
            aria-label="Previous reviews"
          >
            <ChevronLeftIcon className="w-6 h-6 group-hover:scale-110 transition-transform" />
          </button>

          <button 
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 z-10 p-4 bg-white shadow-xl rounded-full text-navy hover:text-mint transition-all duration-300 hidden xl:block border border-gray-100 group"
            aria-label="Next reviews"
          >
            <ChevronRightIcon className="w-6 h-6 group-hover:scale-110 transition-transform" />
          </button>

          {/* Mobile/Tablet Controls */}
          <div className="flex xl:hidden justify-end space-x-4 mb-6 px-4">
            <button 
              onClick={() => scroll('left')}
              className="p-3 bg-white shadow-md rounded-full text-navy hover:text-mint transition-colors border border-gray-100"
              aria-label="Previous reviews"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <button 
              onClick={() => scroll('right')}
              className="p-3 bg-white shadow-md rounded-full text-navy hover:text-mint transition-colors border border-gray-100"
              aria-label="Next reviews"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </div>

          <div 
            ref={scrollRef}
            className="flex space-x-6 overflow-x-auto snap-x snap-mandatory pb-12 px-4 md:px-0 scrollbar-hide"
            style={{ 
              scrollbarWidth: 'none', 
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch' 
            }}
          >
            {reviews.map((review: any, index: number) => (
              <div 
                key={index}
                className="flex-shrink-0 w-[85vw] sm:w-[450px] md:w-[400px] snap-center bg-white p-8 md:p-10 rounded-3xl shadow-[0_15px_40px_rgba(0,0,0,0.04)] border border-gray-50 flex flex-col justify-between h-auto min-h-[400px] transition-transform duration-500 hover:shadow-[0_20px_50px_rgba(0,0,0,0.06)]"
              >
                <div>
                  <div className="flex mb-6">
                    {[...Array(5)].map((_, i) => (
                      <StarIcon key={i} className="w-6 h-6 text-[#FBBC04]" />
                    ))}
                  </div>
                  <div className="text-gray-700 leading-relaxed mb-8 text-lg font-primary relative">
                    <span className="text-5xl text-mint/20 absolute -top-4 -left-2 font-serif">"</span>
                    <p className="relative z-10 italic">
                      {review.text}
                    </p>
                  </div>
                </div>
                <div className="mt-auto pt-6 border-t border-gray-100">
                  <p className="text-mint font-bold text-xl tracking-wide">{review.author}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Google Reviews Link */}
        <div className="text-center mt-8">
          <a 
            href="https://www.google.com/search?sca_esv=e4930b54a8b6c4ed&q=SeaDent+Mallorca+reviews" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 text-navy font-bold uppercase tracking-[0.2em] text-xs md:text-sm hover:text-mint transition-all duration-300 border-b-2 border-mint/30 hover:border-mint pb-2"
          >
            <span>{translations.header.reviews.google}</span>
            <ChevronRightIcon className="w-4 h-4" />
          </a>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}} />
    </section>
  );
};

export default SocialProofSection;
